import {
  useAddOrganizationRequirementMutation,
  useDeleteOrganizationRequirementMutation,
  useDeleteRequirementMutation,
  useGetOrgRequirementsByOrgAndTypeLazyQuery,
  useGetOrganizationByIdLazyQuery,
  useGetRequirementsByTypeLazyQuery,
} from "graphql/__generated__/hooks";
import {
  Messages,
  NotificationSeverity,
  REQUIREMENT,
  TABLE_ACTIONS,
} from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Requirement } from "types/requirement";
import { filterRequirementsForOrgCatalog } from "pages/UploadCSV/UploadCSVRequirements/orgExclusiveTag";
import { filterAdminOnlyRequirements } from "utils/adminOnlyContent";
import { useAuthUser } from "hooks/useAuthUser";

/**
 * hook to share Requirement list functionalities between EPA and competency
 *
 * - requirements: Requirement[] List of requirements
 * - loading: boolean
 * - error: Error
 * - handleActionClick: function Handle when user click an action element.
 * - deleteRequirementOpen: boolean Display the Delete alert.
 * - setDeleteRequirementOpen: function to change state of delete alert.
 * - requirementIdToDelete: id of the Requirement element to delete
 * - handleDelete: Function to delete an element.
 */
export const useRequirement = (reqType: string) => {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const [deleteRequirementOpen, setDeleteRequirementOpen] = useState(false);
  const [requirementIdToDelete, setRequirementIdToDelete] = useState("");
  const { organizationId } = useParams();
  const isOrgView = useMemo(() => {
    return !!organizationId;
  }, [organizationId]);

  const [deleteRequirement] = useDeleteRequirementMutation();
  const { notify } = useNotifications();

  const [
    getRequirementsByType,
    { data, error, loading: requirementsLoading, refetch },
  ] = useGetRequirementsByTypeLazyQuery({
    variables: { reqType },
  });
  const [getOrgRequirementsByOrg, { data: orgData, loading: orgLoading }] =
    useGetOrgRequirementsByOrgAndTypeLazyQuery();
  const [getOrganizationById, { data: organizationData }] =
    useGetOrganizationByIdLazyQuery();

  const [addOrganizationRequirement] = useAddOrganizationRequirementMutation();
  const [deleteOrganizationRequirement] =
    useDeleteOrganizationRequirementMutation();

  const loading = useMemo(() => {
    return requirementsLoading || orgLoading;
  }, [requirementsLoading, orgLoading]);

  const fetchOrgRequirements = useCallback(() => {
    if (organizationId && reqType) {
      getOrgRequirementsByOrg({
        variables: { orgId: organizationId, type: reqType },
      });
    }
  }, [organizationId, reqType]);

  useEffect(() => {
    fetchOrgRequirements();
    getRequirementsByType();
  }, [organizationId, reqType]);

  useEffect(() => {
    if (organizationId) {
      getOrganizationById({ variables: { organizationId } });
    }
  }, [organizationId, getOrganizationById]);

  const organizationName = useMemo(() => {
    return organizationData?.organization_by_pk?.name;
  }, [organizationData]);

  const requirements = useMemo(() => {
    const raw = organizationId
      ? (orgData?.organization_requirement
          .map((or) => or.requirement)
          .filter(Boolean) as Requirement[])
      : (data?.requirement as Requirement[]);
    return filterAdminOnlyRequirements(raw || [], user?.role);
  }, [organizationId, orgData, data, user?.role]);

  const allRequirements = useMemo(() => {
    const catalog = (data?.requirement as Requirement[]) || [];
    const visible = filterAdminOnlyRequirements(catalog, user?.role);
    if (!organizationId) {
      return visible;
    }
    return filterRequirementsForOrgCatalog(visible, organizationName);
  }, [data, organizationId, organizationName, user?.role]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    const getRoutePrefix = () => {
      if (reqType === REQUIREMENT.REQUIREMENT_EPA) return "epa";
      if (reqType === REQUIREMENT.REQUIREMENT_COMPETENCY) return "competency";
      if (reqType === REQUIREMENT.REQUIREMENT_ITER) return "iter";
      return "requirement";
    };

    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/${getRoutePrefix()}/${id}`,
        );
        break;
      case TABLE_ACTIONS.EDIT:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/${getRoutePrefix()}/${id}/edit`,
        );
        break;
      case TABLE_ACTIONS.DELETE:
        setRequirementIdToDelete(id);
        setDeleteRequirementOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!requirementIdToDelete || requirementIdToDelete === "") {
      return;
    }
    if (isOrgView) {
      const orgRequirementIdToDelete = orgData?.organization_requirement.find(
        (or) => or.requirement.requirement_id === requirementIdToDelete,
      )?.org_requirement_id;
      console.log("orgRequirementIdToDelete", orgRequirementIdToDelete);
      await deleteOrganizationRequirement({
        variables: { id: orgRequirementIdToDelete },
      });
      fetchOrgRequirements();
    } else {
      deleteRequirement({
        variables: { id: requirementIdToDelete },
      })
        .then(async () => {
          await refetch();
          setRequirementIdToDelete("");
          notify({
            message: Messages.successRequirementDeleted,
            severity: NotificationSeverity.success,
          });
        })
        .catch((error) => {
          if (error.cause.extensions.code === "constraint-violation") {
            notify({
              message:
                "This competency is currently in use and cannot be deleted.",
              severity: NotificationSeverity.error,
            });
          } else {
            notify({
              message: Messages.errorRequirementDeleted,
              severity: NotificationSeverity.error,
            });
          }
        });
    }
  };

  const handleAddSelectedRequirement = async (requirement: Requirement) => {
    console.log("requirement", requirement);
    if (organizationId) {
      try {
        await addOrganizationRequirement({
          variables: {
            org_id: organizationId,
            requirement_id: requirement.requirement_id,
          },
        });
        fetchOrgRequirements();
      } catch (error) {
        notify({
          message: "Error adding requirement",
          severity: NotificationSeverity.error,
        });
      }
    }
  };

  return {
    requirements,
    allRequirements,
    loading,
    isOrgView,
    organizationId,
    error,
    deleteRequirementOpen,
    requirementIdToDelete,
    handleActionClick,
    handleAddSelectedRequirement,
    setDeleteRequirementOpen,
    handleDelete,
  };
};
